import React, { useRef, useState } from "react";
import { Button, Input, Select, Tooltip } from "antd";
import { Upload, Trash2, X, ImageIcon } from "lucide-react";
import { formatFileFieldDisplay } from "../common/FieldRenderer";

const ACTION_CREATE = "create";
const ACTION_REMOVE_PATH = "remove_path";
const ACTION_DELETE = "delete";

/**
 * Inline image location control: one "Select file" button, then a dropdown (Create / Remove path / Delete).
 * Selecting Remove path or Delete from dropdown runs that action and resets dropdown to Create.
 */
export default function ImageLocationFieldInline({
  label,
  path,
  value,
  onChange,
  onPendingFile,
  pendingFile,
  onDelete,
  onRemovePath,
  disabled,
  isCreate,
}) {
  const [dropdownValue, setDropdownValue] = useState(ACTION_CREATE);
  const fileInputRef = useRef(null);

  const displayValue = value ? formatFileFieldDisplay(value) : "";
  const hasValue = value && String(value).trim();
  const hasPending = pendingFile && pendingFile instanceof File;
  const isPresignedS3Url = (s) => typeof s === "string" && s.includes("X-Amz-");
  const directUrl =
    typeof value === "string" &&
    (value.startsWith("http://") || value.startsWith("https://")) &&
    !isPresignedS3Url(value)
      ? value
      : "";
  const handleDropdownChange = (option) => {
    setDropdownValue(option);
    if (option === ACTION_REMOVE_PATH) {
      onRemovePath?.(path);
    } else if (option === ACTION_DELETE) {
      onDelete?.(path);
    }
    // Stays on Remove path / Delete until user saves or changes selection
  };

  const actionOptions = [
    { value: ACTION_CREATE, label: "Create" },
    { value: ACTION_REMOVE_PATH, label: "Remove path" },
    { value: ACTION_DELETE, label: "Delete", disabled: !hasValue },
  ];

  return (
    <div className="rounded-xl border border-slate-200/90 bg-white shadow-sm hover:shadow transition-shadow p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-600">
          <ImageIcon size={18} />
        </span>
        <span className="text-sm font-semibold text-slate-700">{label}</span>
      </div>

      {(displayValue || hasPending) && (
        <Tooltip title={displayValue || (hasPending ? pendingFile.name : "")}>
          <div className="text-xs font-mono text-slate-500 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100 truncate max-w-full">
            {hasPending ? (
              <span className="text-emerald-600">
                Selected: {pendingFile.name}
              </span>
            ) : (
              displayValue
            )}
          </div>
        </Tooltip>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="small"
          icon={<Upload size={14} />}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className="rounded-lg"
        >
          Select file
        </Button>

        <Select
          value={dropdownValue}
          onChange={handleDropdownChange}
          options={actionOptions}
          disabled={disabled}
          className="min-w-[140px] rounded-lg"
          size="small"
        />
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,.svg,.pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPendingFile?.(path, file);
          e.target.value = "";
        }}
        disabled={disabled}
      />

      <div>
        <div className="text-xs text-slate-500 mb-1">Or paste image URL</div>
        <Input
          size="small"
          placeholder="https://… (e.g. Google image link)"
          value={directUrl}
          onChange={(e) => onChange?.(path, e.target.value)}
          disabled={disabled}
          allowClear
          className="rounded-lg"
        />
      </div>
    </div>
  );
}
